import { QueryClient } from '@tanstack/react-query';
import type { BundleListFilters } from '@/types';
import { ApiError } from './api-client';

/**
 * QueryClient factory (mirrors @bundle/query). Suspense-first, with errors
 * thrown to React error boundaries. Retries skip 4xx (client errors are not
 * transient) but retry transient/5xx failures with backoff.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Suspense mode is opted into per-hook via useSuspenseQuery.
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (error instanceof ApiError) {
            if (error.status >= 400 && error.status < 500) return false;
          }
          return failureCount < 2;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      },
      mutations: {
        retry: false,
      },
    },
  });
}

/**
 * Typed query-key registry. All hooks key off this to prevent cache drift and
 * to make targeted invalidation (from SSE/Push events) unambiguous.
 */
export const queryKeys = {
  bundles: {
    all: ['bundles'] as const,
    list: (filters: BundleListFilters) =>
      [...queryKeys.bundles.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.bundles.all, 'detail', id] as const,
  },
  regions: {
    all: ['regions'] as const,
    bySlug: (slug: string) => [...queryKeys.regions.all, slug] as const,
  },
  orders: {
    all: ['orders'] as const,
    mine: (page: number) => [...queryKeys.orders.all, 'mine', page] as const,
    detail: (id: string) => [...queryKeys.orders.all, 'detail', id] as const,
  },
  auth: {
    me: ['auth', 'me'] as const,
  },
} as const;
